import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ArrowSquareOut, Warning, XCircle, Info, CheckCircle } from "@phosphor-icons/react";

const LEVEL = {
  critical: { color: "#F43F5E", Icon: XCircle, label: "Critical" },
  warning: { color: "#FBBF24", Icon: Warning, label: "Warning" },
  info: { color: "#00B4D8", Icon: Info, label: "Info" },
  pass: { color: "#10B981", Icon: CheckCircle, label: "Pass" },
};

function ScoreRing({ score }) {
  const R = 34, C = 2 * Math.PI * R, dash = (score / 100) * C;
  const col = score >= 80 ? "#10B981" : score >= 50 ? "#C20511" : "#F43F5E";
  return (
    <div className="relative h-[84px] w-[84px] shrink-0" data-testid="qaqc-score">
      <svg viewBox="0 0 84 84" className="h-full w-full -rotate-90">
        <circle cx="42" cy="42" r={R} fill="none" stroke="#334155" strokeOpacity="0.35" strokeWidth="7" />
        <circle cx="42" cy="42" r={R} fill="none" stroke={col} strokeWidth="7" strokeLinecap="round" strokeDasharray={`${dash} ${C}`} style={{ transition: "stroke-dasharray 0.6s ease" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center"><span className="font-mono font-bold text-xl" style={{ color: col }}>{score}</span></div>
    </div>
  );
}

export default function QaQc() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await api.get(`/projects/${id}/qaqc`); setData(r.data); }
      catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const s = data?.summary || {};

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="qaqc" />
      <div className="ml-[64px] flex flex-col min-h-screen">
        <header className="h-[52px] shrink-0 sticky top-0 z-[900] bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-5">
          <ShieldCheck size={18} style={{ color: "#F43F5E" }} className="shrink-0" />
          <h1 className="font-display font-semibold text-sm sm:text-lg tracking-tight truncate min-w-0" data-testid="qaqc-title">QA/QC &amp; Technical Alerts</h1>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => nav(`/projects/${id}/command`)} data-testid="qaqc-back">
              <ArrowSquareOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Command Centre</span>
            </Button>
          </div>
        </header>

        {loading ? (
          <div className="p-6 space-y-3">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-16 rounded-lg bg-card border border-border animate-pulse" />)}</div>
        ) : (
          <div className="max-w-[1200px] mx-auto w-full px-4 sm:px-5 py-5 space-y-5">
            {/* Readiness header */}
            <div className="rounded-lg bg-card border border-border p-5 flex items-center gap-5" data-testid="qaqc-summary">
              <ScoreRing score={data?.score || 0} />
              <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Data Readiness</div>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">Automated technical review of this project's data completeness, imagery coverage and QA-QC checks. All targets remain conceptual until validated by ground sampling.</p>
              </div>
              <div className="hidden sm:flex gap-2">
                {["critical", "warning", "info", "pass"].map((k) => {
                  const L = LEVEL[k];
                  return (
                    <div key={k} className="w-16 rounded-md border p-2 text-center" style={{ borderColor: `${L.color}59`, background: `${L.color}12` }} data-testid={`qaqc-count-${k}`}>
                      <div className="font-mono font-bold text-lg" style={{ color: L.color }}>{s[k] || 0}</div>
                      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{L.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
              {/* Checks */}
              <div className="lg:col-span-2 rounded-lg bg-card border border-border overflow-hidden" data-testid="qaqc-checks">
                <div className="px-4 py-3 border-b border-border font-display font-semibold text-sm">Technical Alerts</div>
                <div className="divide-y divide-border/60">
                  {(data?.checks || []).map((ck, i) => {
                    const L = LEVEL[ck.level] || LEVEL.info;
                    return (
                      <div key={i} className="px-4 py-3 flex items-start gap-3" data-testid={`qaqc-check-${i}`}>
                        <L.Icon size={18} style={{ color: L.color }} className="shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-[3px]" style={{ color: L.color, background: `${L.color}1F` }}>{ck.category}</span>
                            <span className="font-medium text-sm text-foreground">{ck.title}</span>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{ck.detail}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Conformance checklist */}
              <div className="rounded-lg bg-card border border-border overflow-hidden" data-testid="qaqc-jorc">
                <div className="px-4 py-3 border-b border-border font-display font-semibold text-sm">Technical QA-QC Checklist</div>
                <div className="divide-y divide-border/60">
                  {(data?.jorc || []).map((j, i) => (
                    <div key={i} className="px-4 py-2.5 flex items-center gap-2.5" data-testid={`qaqc-jorc-${i}`}>
                      {j.status === "ok"
                        ? <CheckCircle size={16} style={{ color: "#10B981" }} className="shrink-0" />
                        : <Warning size={16} style={{ color: "#FBBF24" }} className="shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm text-foreground">{j.item}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{j.note}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
